/**
 * Default avatar used when a user has not uploaded a personal photo.
 */
export const DEFAULT_USER_AVATAR = '/default-avatar.svg';

const LEGACY_AVATAR_SUBSTRINGS = [
  'testbucketplatforma34/blank.jpg',
  'testbucketplatforma34/woman_blue_background.jpeg',
  'testbucketplatforma34/photo_woman_beauty.webp',
];

/**
 * Google profile pictures (`lh3.googleusercontent.com/...=s96-c`) come from
 * Sign-in-with-Google at the default 96-pixel size. When we render them at
 * 100–200 px on settings/avatar tiles they look like a pixelated mess —
 * especially the auto-generated coloured-square initial avatars Google gives
 * to users with no photo (the brown «D» tile that prompted this fix).
 *
 * Solution: rewrite the trailing `=s<N>` size token to a higher value so
 * Google's CDN serves a crisper crop. Free, no extra request — same URL,
 * different size argument. See:
 * https://developers.google.com/people/image-sizing
 *
 * @param url Raw Google avatar URL.
 * @param size Target pixel size (default 400 — comfortably covers all current
 *   usages without overpaying bandwidth on tiny rail-icons).
 */
export const upscaleGoogleAvatar = (url: string, size = 400): string => {
  if (!url || !/lh\d\.googleusercontent\.com/i.test(url)) return url;
  // Common patterns at end of the URL:
  //   ...=s96-c        (size 96, square cropped)
  //   ...=s40          (size 40, no crop)
  //   ...=s120-c-mo    (size 120, cropped + mask-overlay)
  //   no =sN at all    (full-resolution original — leave alone)
  // Only rewrite when we actually see =sN — never invent a size token.
  return url.replace(/=s\d+(?=([-=][\w-]+)*$)/i, `=s${size}`);
};

/**
 * Normalizes avatar URLs, replacing empty or legacy placeholder values with the bundled default.
 *
 * Also auto-upscales Google profile pictures (see `upscaleGoogleAvatar`) so
 * users who signed up via Google don't see a pixelated 96-px tile.
 *
 * @param candidate Raw avatar URL from the backend.
 * @returns A URL that is safe to render in the UI.
 */
export const getSafeAvatarUrl = (candidate?: string | null): string => {
  const value = (candidate ?? '').trim();
  if (!value) {
    return DEFAULT_USER_AVATAR;
  }

  const lower = value.toLowerCase();
  if (LEGACY_AVATAR_SUBSTRINGS.some(substring => lower.includes(substring))) {
    return DEFAULT_USER_AVATAR;
  }

  return upscaleGoogleAvatar(value);
};
